import { Button, message, Modal, ModalProps, Select } from 'antd';
import { usePlatformSelect } from '../ImplementData/components/Filter/Provider';
import { useMemo, useState } from 'react';
import toLowerCase from 'utils/toLowerCase';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { DatePicker } from 'components/DatePicker';
import StatusButton from 'components/StatusButton';
import { exportMaliciousTrade } from './service';
import { useRequest } from 'ahooks';
import { AdminStore, getAdminStores } from 'services/api/admin';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import styled from '@emotion/styled';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';

function ExportTradeModal(props: ModalProps) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const [platform, setPlatform] = useState<General.PlatformUpperCase>();

  const [region, setRegion] = useState<General.UpperCaseRegion>();

  const [store, setStore] = useState<AdminStore>();

  const [beginDate, setBeginDate] = useState(dayjs().subtract(7, 'days'));
  const [endDate, setEndDate] = useState(dayjs().subtract(1, 'days'));

  const {
    regionInfo: { regionList, regionsMap },
    platformSelect,
  } = usePlatformSelect({
    scene: 'ORDER_STAT',
    noDepartment: true,
    showPlatformAll: true,
    value: platform || 'ALL',
    onChange: (value) => {
      setPlatform(value === 'ALL' ? undefined : value);
      setRegion(undefined);
      setStore(undefined);
    },
  });

  const regions = platform ? regionsMap?.[toLowerCase(platform)] : regionList;

  const { loading, data } = useRequest(
    () => getAdminStores({ page: 1, pageSize: 99999 }).then((res) => res.data.data.stores),
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { config } = useStoreNameConfig();

  const storeOptions = useMemo(() => {
    return data
      ?.filter(
        (store) =>
          (!platform || platform === store.externalType) && (!region || region === store.region),
      )
      .map(({ id, storeName, alias, externalType, region }) => {
        return {
          value: id,
          label: isStoreAliasFirst(config, externalType, region) ? alias || storeName : storeName,
        };
      });
  }, [data, platform, region, config]);

  const { runAsync } = useExportFile();

  return (
    <Modal
      width={800}
      title={t('导出订单明细')}
      footer={[
        <Button onClick={props.onCancel}>{t('取消')}</Button>,
        <StatusButton
          type="primary"
          onClick={() => {
            return runAsync(() =>
              exportMaliciousTrade({
                externalType: platform,
                region,
                externalStoreId: store?.externalId,
                tradeBeginDate: beginDate.format('YYYY-MM-DD'),
                tradeEndDate: endDate.format('YYYY-MM-DD'),
              }),
            );
          }}
        >
          {t('导出')}
        </StatusButton>,
      ]}
      {...props}
    >
      <Container>
        <div>
          {platformSelect}
          <Select
            dropdownMatchSelectWidth={false}
            placeholder={t('站点')}
            value={region || 'ALL'}
            onChange={(value: General.UpperCaseRegion | 'ALL') => {
              setStore(undefined);
              setRegion(value === 'ALL' ? undefined : value);
            }}
            options={[
              {
                value: 'ALL',
                label: t('全部站点'),
              },
              ...(regions || []).map((region) => {
                return {
                  value: region,
                  label: getRegionLabel(region),
                };
              }),
            ]}
          />
          <Select
            virtual
            loading={loading}
            placeholder={t('店铺')}
            value={store?.id || -1000}
            className="!w-[200px]"
            onChange={(value) => {
              if (value === -1000) {
                setStore(undefined);
                return;
              }
              const target = data?.find((_) => _.id === value);
              if (target) {
                setStore(target);
              }
            }}
            options={[
              {
                value: -1000,
                label: t('全部店铺'),
              },
              ...(storeOptions || []).map((store) => {
                return {
                  value: store.value,
                  label: store.label,
                };
              }),
            ]}
          />
        </div>
        <div>
          <span>{t('下单时间')}</span>
          <DatePicker.RangePicker
            value={[beginDate, endDate]}
            onChange={(range) => {
              if (range) {
                const [start, end] = range;
                if (start && end) {
                  setBeginDate(start);
                  setEndDate(end);
                }
              }
            }}
          />
        </div>
      </Container>
    </Modal>
  );
}

const Container = styled.div`
  border: solid 1px var(--fett-border-default);
  > div {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    > * {
      margin-right: 16px;
    }
  }
  > div + div {
    border-top: solid 1px var(--fett-border-default);
  }
`;

export default ExportTradeModal;
